<template>
    <div class="ym-picker-root relative mobile-full-root">
        <div v-if="!hideInput">
            <BaseButton variant="primary" class="w-100" @click="toggleShow">
                {{
                    year !== -1 && month !== -1
                        ? ''.concat(year, ' - ', getMonthText(month))
                        : defaultText
                }}
            </BaseButton>
        </div>
        <transition name="appear">
            <div
                v-if="show"
                class="ym-picker-modal mobile-full-modal d-flex flex-column"
                :class="{
                    'anchor-bottom': anchor === 'bottom',
                    'anchor-top': anchor === 'top',
                    'mobile-anchor-top': mobileAnchor
                        ? mobileAnchor === 'top'
                        : anchor === 'top',
                    'mobile-anchor-bottom': mobileAnchor
                        ? mobileAnchor === 'bottom'
                        : anchor === 'bottom',
                }"
                v-on-clickaway="toggleShow"
            >
                <div class="ym-header d-flex">
                    <div
                        class="arrows pointer d-flex align-items-center"
                        @click="() => shiftYears(-1)"
                    >
                        <BaseIcon name="chevron-left" size="lg" />
                    </div>
                    <div
                        class="flex-grow d-flex align-items-center justify-content-center pointer noselect"
                        @click="changeMode"
                    >
                        <h6 class="text-center mb-0 font-weight-bold">
                            {{ year }}
                        </h6>
                    </div>
                    <div
                        class="arrows pointer d-flex align-items-center flex-row-reverse"
                        @click="() => shiftYears(1)"
                    >
                        <BaseIcon name="chevron-right" size="lg" />
                    </div>
                </div>
                <Divider />
                <div class="content">
                    <div v-if="mode === 0" class="month-content">
                        <div
                            v-for="m in months"
                            :key="m"
                            class="noselect clickable"
                            @click="changeMode"
                        >
                            <div
                                class="item p-3 pointer"
                                :class="{ 'item-selected': m == month }"
                                @click="() => onMonthClick(m)"
                            >
                                <h6 class="mb-0 text-center">
                                    {{ getMonthText(m) }}
                                </h6>
                            </div>
                        </div>
                    </div>
                    <div v-else class="year-content">
                        <div
                            v-for="y in years"
                            :key="y"
                            class="noselect clickable"
                            @click="changeMode"
                        >
                            <div
                                class="item p-3 pointer"
                                :class="{ 'item-selected': y == year }"
                                @click="() => onYearClick(y)"
                            >
                                <h6 class="mb-0 text-center">{{ y }}</h6>
                            </div>
                        </div>
                    </div>
                </div>
                <Divider />
                <div class="flex-grow" />
                <div
                    v-if="!disableDoneBtn"
                    class="toggle-btn py- px-3 pointer noselect primary"
                    @click="toggleShow"
                >
                    <h6 class="text-center mb-0 white w-100">
                        {{ $t('general.done') }}
                    </h6>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
// Utils
import { mixin as clickaway } from 'vue-clickaway';
import i18n from '~/i18n';

const YEARS_PER_PAGE = 16;
const MONTH_MODE = 0;
const YEAR_MODE = 1;
const NO_VALUE = -1;
const MIN_YEAR = 1900;
export default {
    name: 'YearMonthPicker',
    mixins: [clickaway],
    props: {
        value: {
            // The date
            type: [Date, String],
            default: () => new Date(),
        },
        defaultText: {
            type: String,
            default: i18n.t(
                'components.builders.yearMonthPicker.defaultSelect'
            ),
        },
        hideInput: Boolean,
        open: Boolean, // Is for controlling data.show with a prop,
        disableDoneBtn: Boolean, // Disables the done button at the bottom
        anchor: {
            type: String,
            default: 'bottom',
        },
        mobileAnchor: String,
    },
    data: () => ({
        show: false,
        mode: MONTH_MODE, //

        months: new Array(12).fill().map((_, i) => i), // Months by index (0 - 11)
        years: new Array(YEARS_PER_PAGE)
            .fill()
            .map((_, i) => new Date().getFullYear() + (i - YEARS_PER_PAGE / 2)),

        month: -1, // Selected month
        year: new Date().getFullYear(), // Selected year
    }),
    methods: {
        toggleShow() {
            this.show = !this.show;
            if (this.show) {
                this.$emit('open');
            } else if (!this.show) {
                this.$emit('close');
            }
        },
        getMonthText(monthIndex) {
            // Validate month index
            monthIndex =
                monthIndex >= this.months.length
                    ? this.months.length - 1
                    : monthIndex <= 0
                    ? 0
                    : monthIndex;
            const monthText = this.$t('general.months') || [];
            return monthText[monthIndex];
        },
        onMonthClick(monthIndex) {
            // Validate month index
            monthIndex =
                monthIndex >= this.months.length
                    ? this.months.length - 1
                    : monthIndex <= 0
                    ? 0
                    : monthIndex;
            this.month = monthIndex;
            this.emit();
        },
        onYearClick(year) {
            this.year = year;
            this.emit();
        },
        changeMode() {
            this.mode = this.mode === MONTH_MODE ? YEAR_MODE : MONTH_MODE;
        },
        shiftYears(direction) {
            // If month mode, change year by direction step (1 or -1)
            if (this.mode === MONTH_MODE) {
                // Make sure the user can not select a year less than the MIN_YEAR
                if (direction < 0 && this.year <= MIN_YEAR) {
                    return;
                }
                this.year += direction;
                this.emit();
                return;
            }

            // Make sure the user can not select a year less than the MIN_YEAR
            if (
                direction < 0 &&
                this.years[0] + YEARS_PER_PAGE * direction <= MIN_YEAR
            ) {
                return;
            }

            // Shift years by YEARS_PER_PAGE in given direction.
            this.years = this.years.map(
                year => year + YEARS_PER_PAGE * direction
            );
        },
        updateDate(newDate) {
            if (!newDate) {
                this.month = NO_VALUE;
                this.year = new Date().getFullYear();
                return;
            }
            const date = new Date(newDate);
            this.month = date.getMonth();
            this.year = date.getFullYear();
        },
        getDate() {
            const date = new Date(
                this.year === NO_VALUE ? 1970 : this.year,
                this.month === NO_VALUE ? 0 : this.month
            );
            return date;
        },
        emit() {
            this.$emit('input', this.getDate());
        },
    },
    mounted() {
        this.updateDate(this.value);
    },
    watch: {
        value(newDate) {
            this.updateDate(newDate);
        },
        open(newOpen, oldOpen) {
            this.show = Boolean(newOpen);
            if (this.show) {
                this.$emit('open');
            } else if (!this.show) {
                this.$emit('close');
            }
        },
    },
};
</script>

<style scoped>
.ym-picker-root {
    position: relative;
}

.ym-picker-modal {
    background-color: white;
    box-shadow: 2px 2px 5rem 0 rgba(0, 0, 0, 0.4);

    z-index: 10000;
    min-width: 300px;

    position: absolute;
}

.anchor-top {
    top: 0;
}
.anchor-bottom {
    bottom: 0;
}

.ym-header {
    height: 44px;
}

.content {
    background-color: rgb(245, 245, 245);
}

.month-content {
    display: -ms-grid;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: 1px;
}

.year-content {
    display: -ms-grid;
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-gap: 1px;
}

.item {
    background-color: white;
    touch-action: manipulation;
}

.item-selected {
    background-color: var(--primary) !important;
    color: white !important;
}
.item-selected h6 {
    color: white !important;
}

.toggle-btn {
    min-height: 44px;
    display: flex;
    align-items: center;
}

.arrows {
    min-width: 60px;
    padding: 0 4px;
}

@media only screen and (max-width: 600px) {
    .year-content {
        grid-template-columns: repeat(4, 1fr);
    }

    .ym-picker-modal {
        padding-bottom: 44px;
    }

    .mobile-full-root {
        position: static !important;
    }

    .mobile-full-modal {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
    }

    .toggle-btn {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
    }

    .mobile-anchor-top {
        top: 0 !important;
    }

    .mobile-anchor-bottom {
        bottom: 0 !important;
    }
}
</style>
